import React, { useContext, useReducer } from "react";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Link from "@mui/material/Link";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import { object, string, ref } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { signInUserFn, getUserFn } from "../APIServices/Services";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import Spinner from "../ui/Spinner";
import { StateContext, useStateContext } from "../context/index";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  logo: {
    height: "7em",
    [theme.breakpoints.down("lg")]: {
      height: "7em",
    },
    [theme.breakpoints.down("sm")]: {
      height: "5.5em",
    },
    width: 150,
  },
  signInButton: {
    backgroundColor: theme.palette.common.blue,
    "&:hover": {
      backgroundColor: theme.palette.common.blue,
    },
  },
}));

const theme = createTheme();

export default function Login() {
  const classes = useStyles();
  const validate = object().shape({
    username: string()
      .min(1, "Username is required")
      .max(20, "Username is too long"),
    email: string()
      .min(1, "Email address is required")
      .email("Email Address is invalid"),
    password: string()
      .min(1, "Password is required")
      .min(8, "Password must be more than 8 characters")
      .max(32, "Password must be less than 32 characters")
      .matches(
        /^.*(?=.{8,})((?=.*[!@#$%^&*()\-_=+{};:,<.>]){1})(?=.*\d)((?=.*[a-z]){1})((?=.*[A-Z]){1}).*$/,
        "Password must contain at least 8 characters, one uppercase, one number and one special case character"
      ),
  });

  const methods = useForm({
    resolver: yupResolver(validate),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitSuccessful },
  } = methods;
  useEffect(() => {
    if (isSubmitSuccessful) {
      reset();
    }
  }, [reset, isSubmitSuccessful]);

  const navigate = useNavigate();

  const { dispatch } = useStateContext();

  // API Get Current Logged in user
  const query = useQuery(["authUser"], getUserFn, {
    enabled: false,
    select: (data) => data.data.user,
    retry: 1,
    onSuccess: (data) => {
      dispatch({ type: "SET_AUTH_USER", payload: data });
    },
    onError: (error) => {
      if (Array.isArray(error.response.data.error)) {
        error.response.data.error.forEach((el) =>
          toast.error(el.message, {
            position: "top-right",
          })
        );
      } else {
        toast.error(error.response.data.message, {
          position: "top-right",
        });
      }
    },
  });

  // API Login Mutation
  const { mutate, isLoading, isError, error } = useMutation(
    (username, email, password) => {
      signInUserFn(username, email, password);
    },
    {
      onSuccess: () => {
        query.refetch();
        toast.success("You successfully logged in!");
        navigate("/profile");
      },
      onError: (error) => {
        if (Array.isArray(error.response.data.error)) {
          error.response.data.error.forEach((el) =>
            toast.error(el.message, {
              position: "top-right",
            })
          );
        } else {
          toast.error(error.response.data.message, {
            position: "top-right",
          });
        }
      },
    }
  );
  const onSubmitHandler = (username, email, password) => {
    mutate(username, email, password);
  };

  return (
    <ThemeProvider theme={theme}>
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginBottom: "2em",
          }}
        >
          <Typography component="h1" variant="h4">
            Sign in
          </Typography>
          {isError && (
            <Typography component="h1" variant="h4">
              An error occurred: {error}
            </Typography>
          )}
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmitHandler)}
            noValidate
            sx={{ mt: 1 }}
          >
            <TextField
              autoComplete="given-name"
              type="text"
              name="userName"
              required
              fullWidth
              id="userName"
              label="User Name"
              {...register("username")}
              error={errors.username ? true : false}
              autoFocus
              disabled={isLoading}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              {...register("email")}
              error={errors.email ? true : false}
              name="email"
              autoComplete="email"
              disabled={isLoading}
              autoFocus
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              error={errors.password ? true : false}
              {...register("password")}
              type="password"
              id="password"
              autoComplete="current-password"
              disabled={isLoading}
            />

            <FormControlLabel
              control={<Checkbox value="remember" color="primary" />}
              label="Remember me"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              className={classes.signInButton}
            >
              Sign In
            </Button>
            {isLoading && <Spinner />}
            <Grid container>
              <Grid item xs>
                <Link href="/resetpassword" variant="body2">
                  Forgot password?
                </Link>
              </Grid>
              <Grid item>
                <Link href="/register" variant="body2">
                  {"Don't have an account? Sign Up"}
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Container>
    </ThemeProvider>
  );
}
